import * as React from 'react'
import { Controller, useForm } from 'react-hook-form'

import { Favorite, FavoriteBorder } from '@mui/icons-material'
import { IconButton, Tooltip } from '@mui/material'

import { SearchIcon, UserGroupIcon } from '@/assets/icons'
import { AddToFavoritesModalSkeleton } from '@/components/Favorite/FavoriteList/AddToFavoritesModalSkeleton'
import { Button } from '@/components/ui/tailwind/Button'
import { Modal } from '@/components/ui/tailwind/Modal'
import { Paragraph } from '@/components/ui/tailwind/Paragraph'
import { SearchBarInput } from '@/components/ui/tailwind/SearchBarInput'
import { PAGE_SIZE_MAX } from '@/config/settings'
import { useAddProductToFavoriteList } from '@/hooks/bff/favorite/addProductToFavoriteList/useAddProductToFavoriteList'
import { useGetAllFavoriteListIdsByProduct } from '@/hooks/bff/favorite/getAllFavoriteListIdsByProducts/useGetAllFavoriteListIdsByProduct'
import { useGetMultipleFavoriteList } from '@/hooks/bff/favorite/getMultipleFavoriteList/useGetMultipleFavoriteList'
import { useRemoveProductFromFavoriteList } from '@/hooks/bff/favorite/removeProductFromFavoriteList/useRemoveProductFromFavoriteList'
import { useTranslations } from '@/hooks/useTranslations'
import { SortByEnum } from '@/schemas/sortDirectionEnumSchema'

import type { FilterParams } from '@/containers/MultipleFavoriteListContainer'
import type { SortByEnumSchema } from '@/schemas/sortDirectionEnumSchema'

interface Props {
  productCode: string
  disabled?: boolean
  isInFavoriteList?: boolean
}

interface FormValues {
  sortBy: SortByEnumSchema
  searchText: string
  sortCreatedBy: string
}

export const AddToFavorites: React.FC<Props> = ({ productCode, disabled, isInFavoriteList = false }) => {
  const { translate } = useTranslations()
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const { mutate: addProductToList, isPending: isPendingAddToList } = useAddProductToFavoriteList()
  const { mutate: removeProductFromList, isPending: isPendingRemoveFromList } = useRemoveProductFromFavoriteList()

  const { favoriteListIdsByProduct: favoriteListIdsByProductMap, isFetching: isLoadingFavoriteListIds } =
    useGetAllFavoriteListIdsByProduct(isModalOpen ? [productCode] : [], { refetchOnMount: 'always' })
  const favoriteListIdsByProduct = favoriteListIdsByProductMap[productCode]

  const isDisabled = disabled || isPendingAddToList || isPendingRemoveFromList || isLoadingFavoriteListIds

  const [filterParams, setFilterParams] = React.useState<FilterParams>({
    page: 0,
    pageSize: PAGE_SIZE_MAX,
    sortBy: SortByEnum.NAME_ASC,
    sortCreatedBy: '',
    searchText: '',
  })

  const {
    data: multipleFavoriteList,
    refetch: refetchMultipleFavoriteList,
    isLoading,
  } = useGetMultipleFavoriteList(
    filterParams.page,
    filterParams.pageSize,
    filterParams.sortBy,
    filterParams.searchText,
    filterParams.sortCreatedBy,
    { enabled: isModalOpen }
  )

  const noResults = multipleFavoriteList?.results.length === 0

  const { control } = useForm<FormValues>({
    defaultValues: {
      sortBy: SortByEnum.NAME_ASC,
      searchText: '',
      sortCreatedBy: '',
    },
  })

  const onSearch = (searchText: string) => {
    setFilterParams((prev) => ({ ...prev, searchText, page: 0 }))
    refetchMultipleFavoriteList()
  }

  const handleOpen = () => {
    setIsModalOpen(true)
  }

  const handleClose = () => setIsModalOpen(false)

  const handleAddToFavorites = (favoriteListId: string) => {
    addProductToList({
      favoriteListId,
      productIds: [productCode],
    })
  }

  const handleRemoveFromFavorites = (listId: string) => {
    removeProductFromList({ productId: productCode, favoriteListId: listId })
  }

  const toggleFavorite = async (favoriteListId: string) => {
    const isInFavorites = favoriteListIdsByProduct?.includes(favoriteListId)

    if (isInFavorites) {
      handleRemoveFromFavorites(favoriteListId)
    } else {
      handleAddToFavorites(favoriteListId)
    }
  }

  return (
    <>
      <Tooltip title={translate('favoriteList.addToFavorites')}>
        <span>
          <IconButton onClick={handleOpen} disabled={isDisabled} color="secondary" sx={{ padding: 0 }}>
            {isInFavoriteList ? <Favorite /> : <FavoriteBorder />}
          </IconButton>
        </span>
      </Tooltip>

      <Modal open={isModalOpen} onClose={handleClose} title={translate('favoriteList.selectList')}>
        <div className="flex flex-col justify-between gap-4 lg:flex-row">
          <Controller
            name="searchText"
            control={control}
            render={({ field }) => (
              <SearchBarInput
                value={field.value}
                onChange={field.onChange}
                onAction={() => onSearch(field.value)}
                placeholder={translate('favourites.filter.placeholder')?.toString()}
                icon={SearchIcon}
              />
            )}
          />
        </div>

        <div className="mt-4 mb-4 max-h-68 overflow-y-auto border-border-alternative border-b">
          {isLoading && <AddToFavoritesModalSkeleton />}
          {noResults && <p>{translate('favoriteList.list.noResults')}</p>}

          {multipleFavoriteList?.results.map((item) => {
            const isInThisList = favoriteListIdsByProduct?.includes(item.id)

            return (
              <div
                key={item.id}
                className="flex w-full flex-row items-center justify-between border-border-default border-t px-4 py-2 no-underline first:border-t-0 last:border-b hover:bg-input-active"
              >
                <div className="flex flex-col">
                  <Paragraph variant="body" className="text-body-text-default">
                    {item.name}
                  </Paragraph>
                  <Paragraph
                    variant="bodySmall"
                    className="flex flex-row items-center gap-1 text-body-text-alternative"
                  >
                    <UserGroupIcon className="m-0 size-4 text-body-text-alternative" />
                    <span>
                      {translate(`favoriteList.createdBy`, {
                        userNameCreated: item?.createdBy || translate('favourites.list.systemName'),
                        userNameModified: item?.lastModifiedBy || translate('favourites.list.systemName'),
                      })}
                    </span>
                  </Paragraph>
                </div>

                <Tooltip
                  title={
                    !isDisabled &&
                    (!isInThisList
                      ? translate('productDetails.addToFavorite')
                      : translate('productDetails.removeFromFavorite'))
                  }
                >
                  <span>
                    <IconButton
                      onClick={() => toggleFavorite(item.id)}
                      disabled={isDisabled}
                      color="secondary"
                      sx={{ padding: 0 }}
                    >
                      {isInThisList ? <Favorite /> : <FavoriteBorder />}
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            )
          })}
        </div>
        <Button variant="outline" onClick={handleClose}>
          {translate('favoriteList.actions.cancel')}
        </Button>
      </Modal>
    </>
  )
}
